import { html, nothing, type TemplateResult } from 'lit';
import {
  DIMENSION_LABELS,
  formatMapLink,
  type ReleaseManifest,
  type PublicEpisode,
  type PublicCharacter,
  type CanonStatus,
} from '@ka/contracts';
import { spiderSvg, type KaYouTube } from '@ka/ui';
import {
  CHARACTERS_HEADING,
  LORE_HEADING,
  SITE_INTRO,
  SITE_TAGLINE,
  SITE_TITLE,
} from '@ka/contracts/canon';
import { assetUrl, byNumberDesc } from './data.ts';
import type { Route } from './router.ts';

export interface Page {
  title: string;
  body: TemplateResult;
}

const fmtTime = (t: number) => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

const canonLabel: Record<CanonStatus, string> = {
  established: 'Established canon',
  'draft-idea': 'Idea, not yet canon',
  'open-question': 'Open question',
};

export const notFound = (): Page => ({
  title: 'Lost in the caves',
  body: html`<section class="panel">
    <h1>Lost in the caves</h1>
    <p>This tunnel leads nowhere. <a href="/">Return to the surface</a>.</p>
  </section>`,
});

export const comingSoon = (title: string): Page => ({
  title,
  body: html`<section class="panel">
    <h1>${title}</h1>
    <p>Nothing here yet — the first adventure is on its way. Check back soon!</p>
    <p><a href="/">Back to the home page</a></p>
  </section>`,
});

function episodeCard(m: ReleaseManifest, e: PublicEpisode) {
  const thumb = assetUrl(m, e.thumbnailAssetId);
  return html`<li class="book">
    <a href="/episodes/${e.slug}">
      ${thumb ? html`<img src=${thumb} alt="" loading="lazy" width="320" height="180" />` : html`<div class="book-cover" aria-hidden="true">${spiderSvg(40)}</div>`}
      <span class="book-num">Episode ${e.number}</span>
      <span class="book-title">${e.title}</span>
    </a>
  </li>`;
}

function characterCard(m: ReleaseManifest, c: PublicCharacter) {
  const art = assetUrl(m, c.artworkAssetId);
  return html`<li class="panel char accent-${c.accent}">
    ${art ? html`<img class="art" src=${art} alt="" loading="lazy" />` : nothing}
    <h3><a href="/characters/${c.slug}">${c.displayName}</a></h3>
    <p>${c.tagline}</p>
  </li>`;
}

export function home(m: ReleaseManifest | null): Page {
  const latest = m ? [...m.episodes].sort(byNumberDesc)[0] : undefined;
  return {
    title: SITE_TITLE,
    body: html`
      <section class="hero">
        <p class="kicker">A Minecraft adventure</p>
        <h1>${SITE_TITLE}</h1>
        <p class="tagline">${SITE_TAGLINE}</p>
        ${SITE_INTRO.map((p) => html`<p class="lede">${p}</p>`)}
        <div class="ctas">
          ${latest ? html`<a class="btn primary" href="/episodes/${latest.slug}">▶ Watch Latest Adventure</a>` : nothing}
          <a class="btn green" href="/map">Explore the World</a>
        </div>
      </section>
      ${
        latest
          ? html`<section>
              <h2>Latest adventure</h2>
              <ul class="books">
                ${episodeCard(m!, latest)}
              </ul>
            </section>`
          : html`<section class="panel">
              <h2>The first adventure is on its way</h2>
              <p>Episodes will appear here once they are published. Check back soon!</p>
            </section>`
      }
      ${
        m?.characters.length
          ? html`<section>
              <h2>${CHARACTERS_HEADING}</h2>
              <ul class="grid plain">
                ${m.characters.map((c) => characterCard(m, c))}
              </ul>
            </section>`
          : nothing
      }
      <ka-spider-symbol symbol="home" class="hidden-spider"></ka-spider-symbol>
    `,
  };
}

export function episodes(m: ReleaseManifest): Page {
  const list = [...m.episodes].sort(byNumberDesc);
  return {
    title: 'Episodes',
    body: html`<h1>Episodes</h1>
      ${
        list.length
          ? html`<ul class="books">
              ${list.map((e) => episodeCard(m, e))}
            </ul>`
          : html`<p class="panel">No episodes yet — the first adventure is coming soon.</p>`
      }
      <ka-spider-symbol symbol="episodes" class="hidden-spider"></ka-spider-symbol>`,
  };
}

export function episode(m: ReleaseManifest, r: Route): Page {
  const e = m.episodes.find((x) => x.slug === r.params.slug);
  if (!e) return notFound();
  const landmarks = m.landmarks.filter((l) => e.landmarkIds.includes(l.id));
  const seek = (t: number) => (ev: Event) => {
    const root = (ev.target as Element).getRootNode() as ParentNode;
    root.querySelector<KaYouTube>('ka-youtube')?.playAt(t);
  };
  return {
    title: `Episode ${e.number}: ${e.title}`,
    body: html`
      <p class="kicker"><a href="/episodes">Episodes</a> › Episode ${e.number}</p>
      <h1>${e.title}</h1>
      <ka-youtube
        video-id=${e.youtubeId ?? ''}
        title=${e.title}
        thumbnail=${assetUrl(m, e.thumbnailAssetId) ?? ''}
      ></ka-youtube>
      <div class="two-col">
        <div>
          <ka-markdown .source=${e.summary}></ka-markdown>
          ${
            e.chapters.length
              ? html`<h2>Chapters</h2>
                  <ol class="chapters">
                    ${e.chapters.map((c) => html`<li><button class="link" @click=${seek(c.t)}>${fmtTime(c.t)}</button> ${c.label}</li>`)}
                  </ol>`
              : nothing
          }
        </div>
        <aside class="panel">
          <h2>Places in this episode</h2>
          ${
            landmarks.length
              ? html`<ul>
                  ${landmarks.map((l) => html`<li><a href="/map?${formatMapLink({ snapshot: e.map?.snapshotId, landmark: l.id })}">${l.name}</a></li>`)}
                </ul>`
              : html`<p class="muted">No landmarks marked yet.</p>`
          }
          ${
            e.map
              ? html`<p>
                  <a class="btn green" href="/map?${formatMapLink({ snapshot: e.map.snapshotId })}"
                    >See the world after Episode ${e.number}</a
                  >
                </p>`
              : html`<p class="muted">No map snapshot for this episode.</p>`
          }
        </aside>
      </div>
    `,
  };
}

export function characters(m: ReleaseManifest): Page {
  return {
    title: 'Characters',
    body: html`<h1>${CHARACTERS_HEADING}</h1>
      <ul class="grid plain">
        ${m.characters.map((c) => characterCard(m, c))}
      </ul>`,
  };
}

export function character(m: ReleaseManifest, r: Route): Page {
  const c = m.characters.find((x) => x.slug === r.params.slug);
  if (!c) return notFound();
  const skin = assetUrl(m, c.skinAssetId);
  const art = assetUrl(m, c.artworkAssetId);
  const lore = m.lore.filter((l) => l.characterIds.includes(c.id));
  return {
    title: c.displayName,
    body: html`
      <p class="kicker"><a href="/characters">Characters</a></p>
      <div class="two-col char-page accent-${c.accent}">
        <div class="char-media">
          ${art ? html`<img class="art" src=${art} alt="${c.displayName}, artwork" />` : nothing}
          <ka-skin-viewer
            skin=${skin ?? ''}
            model=${c.modelType}
            label="${c.displayName} in Minecraft"
          >
            <div class="placeholder">
              ${spiderSvg(64)}
              <p>Placeholder — the approved skin hasn't been added yet.</p>
            </div>
          </ka-skin-viewer>
        </div>
        <div>
          <h1>${c.displayName}</h1>
          <p><span class="tag">${canonLabel[c.canonStatus]}</span></p>
          <ka-markdown .source=${c.biography}></ka-markdown>
          ${
            c.unknowns.length
              ? html`<h2>Unsolved mysteries</h2>
                  <ul>
                    ${c.unknowns.map((u) => html`<li>${u}</li>`)}
                  </ul>`
              : nothing
          }
          ${
            lore.length
              ? html`<h2>Lore</h2>
                  <ul>
                    ${lore.map((l) => html`<li><a href="/lore/${l.slug}">${l.title}</a></li>`)}
                  </ul>`
              : nothing
          }
        </div>
      </div>
    `,
  };
}

export function lore(m: ReleaseManifest, rewardIds: Set<string>, secretPanel: unknown): Page {
  const visible = m.lore.filter((l) => !rewardIds.has(l.id));
  return {
    title: 'Lore',
    body: html`<h1>${LORE_HEADING}</h1>
      <ul class="grid plain">
        ${visible.map(
          (l) =>
            html`<li class="panel">
              <h3><a href="/lore/${l.slug}">${l.title}</a></h3>
              <span class="tag">${canonLabel[l.canonStatus]}</span>
            </li>`,
        )}
      </ul>
      ${secretPanel}
      <ka-spider-symbol symbol="lore" class="hidden-spider"></ka-spider-symbol>`,
  };
}

export function loreEntry(m: ReleaseManifest, r: Route): Page {
  const l = m.lore.find((x) => x.slug === r.params.slug);
  if (!l) return notFound();
  const chars = m.characters.filter((c) => l.characterIds.includes(c.id));
  const places = m.landmarks.filter((x) => l.landmarkIds.includes(x.id));
  return {
    title: l.title,
    body: html`<article class="panel scroll">
      <p class="kicker"><a href="/lore">Lore</a></p>
      <h1>${l.title}</h1>
      <p><span class="tag">${canonLabel[l.canonStatus]}</span></p>
      <ka-markdown .source=${l.body}></ka-markdown>
      ${chars.length ? html`<p>Characters: ${chars.map((c, i) => html`${i ? ', ' : ''}<a href="/characters/${c.slug}">${c.displayName}</a>`)}</p>` : nothing}
      ${
        places.length
          ? html`<p>
              Places:
              ${places.map((p, i) => html`${i ? ', ' : ''}<a href="/map?${formatMapLink({ landmark: p.id })}">${p.name}</a> (${DIMENSION_LABELS[p.dimension]})`)}
            </p>`
          : nothing
      }
    </article>`,
  };
}

export function about(m: ReleaseManifest | null): Page {
  return {
    title: 'About',
    body: html`<section class="panel">
      <h1>About</h1>
      <p>
        King Arachnid Chronicles is a family project: a father and son recording their Minecraft
        adventures as King Arachnid and Kynorum.
      </p>
      <p>The map shows saved snapshots of our world after each episode. It is never live.</p>
      <p class="muted">
        An unofficial fan project. Not an official Minecraft product. Not approved by or associated
        with Mojang or Microsoft.
      </p>
      ${m?.publishedAt ? html`<p class="muted">Last updated ${fmtDate(m.publishedAt)}.</p>` : nothing}
    </section>`,
  };
}

/** The "For parents" page the printed card's QR points to. Plain language on purpose. */
export const parents = (invited: boolean): Page => ({
  title: 'For parents',
  body: html`<section class="panel">
      <h1>For parents</h1>
      <p>
        King Arachnid Chronicles is a family project: a father and son share their Minecraft
        adventures as videos, a map of their world and the story behind the characters. Anyone can
        read the site without an account.
      </p>
      <p>
        Friends we invite can create a <strong>family account</strong> so their kids keep their
        spider-hunt progress across devices. Later, friends will also see new episodes early and can
        suggest landmarks and post short messages — always checked by us before anyone else sees
        them.
      </p>
      ${
        invited
          ? html`<p>
              <strong>You have been invited!</strong> Your card’s code is filled in for you.
            </p>`
          : html`<p>
              Family accounts are by invitation: each invite card has its own QR code, which works
              for one family.
            </p>`
      }
      <p class="ctas">
        <a class="btn ${invited ? 'primary' : ''}" href="/parents/sign-up"
          >${invited ? 'Create your family account' : 'I have an invite code'}</a
        >
        <a class="btn" href="/parents/sign-in">Sign in</a>
      </p>
    </section>
    <section class="panel">
      <h2>How it works</h2>
      <ol>
        <li>
          You scan the QR code on your invite card and sign up with your own email and a passkey or
          password.
        </li>
        <li>You add each kid with a nickname picked from a list (like “BraveSpider7”).</li>
        <li>
          You make a one-time pairing code (or QR code) and enter it on your kid’s device. It works
          once and expires after 15 minutes.
        </li>
        <li>Lost a device? Sign it out from your family page and pair again.</li>
      </ol>
      <p>Kids never type an email or password, and there is no private messaging between kids.</p>
    </section>
    <section class="panel" id="privacy">
      <h2>Privacy notice for parents</h2>
      <p class="tag fixture">DRAFT — to be reviewed before families are invited</p>
      <h3>What we keep</h3>
      <ul>
        <li>Your email address, only to sign you in. It is never shown to anyone.</li>
        <li>Your passkey’s public key or a hash of your password (handled by Amazon Cognito).</li>
        <li>Each kid’s nickname from our word list, and their spider-hunt progress.</li>
        <li>
          A short activity log (which account added, paired or removed a profile, and when) with
          account ids, not names or emails, and which invite card was used.
        </li>
      </ul>
      <h3>What we never collect</h3>
      <p>
        Kids’ real names, emails, birthdays, schools, photos or locations. No ads, no ad tracking
        and no analytics. The only cookie is the one that remembers a paired kid device.
      </p>
      <h3>Where it is stored</h3>
      <p>On Amazon Web Services in Canada (Central). We do not sell or share it.</p>
      <h3>Your choices</h3>
      <p>
        From your family page you can remove a kid profile or delete your whole family account at
        any time. Removal deletes the profile and its progress right away. Questions:
        <em>[contact email to be added]</em>.
      </p>
    </section>`,
});

export { fmtDate, fmtTime };
